import React, { useEffect, useMemo, useState } from 'react';
import { api } from '../lib/api';

interface ConversationBrowserProps {
  token: string;
}

type ConvStatusFilter = 'active' | 'archived' | 'all';

interface KnowledgeBase {
  id: number;
  name: string;
}

interface Conversation {
  id: string;
  kbId: number;
  title: string | null;
  status: 'active' | 'archived';
  messageCount: number;
  summary: string | null;
  externalConversationId: string | null;
  externalUserId: string | null;
  channel: string | null;
  conversationState?: Record<string, any> | null;
  lastMessageAt: string | null;
  createdAt: string;
  updatedAt: string;
  insufficientContextCount?: number;
  hasInsufficientContext?: boolean;
}

interface Message {
  id: number;
  role: 'user' | 'assistant' | 'system';
  content: string;
  metadata?: any;
  createdAt: string;
}

const ConversationBrowser: React.FC<ConversationBrowserProps> = ({ token }) => {
  const authHeaders = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const [knowledgeBases, setKnowledgeBases] = useState<KnowledgeBase[]>([]);
  const [kbLoading, setKbLoading] = useState(true);

  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [convLoading, setConvLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [error, setError] = useState<string | null>(null);

  const [kbId, setKbId] = useState<number | 'all'>('all');
  const [status, setStatus] = useState<ConvStatusFilter>('active');
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [externalUserId, setExternalUserId] = useState('');
  const [debouncedExternalUserId, setDebouncedExternalUserId] = useState('');
  const [insufficientOnly, setInsufficientOnly] = useState(false);

  const [activeConversation, setActiveConversation] = useState<Conversation | null>(null);
  const [stateModalConversation, setStateModalConversation] = useState<Conversation | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [messagesError, setMessagesError] = useState<string | null>(null);

  const limit = 50;

  useEffect(() => {
    const loadKBs = async () => {
      setKbLoading(true);
      try {
        const res = await api.get('/api/kb/list', { headers: authHeaders });
        setKnowledgeBases(res.data.knowledgeBases || []);
      } catch (e) {
        console.error(e);
      } finally {
        setKbLoading(false);
      }
    };
    loadKBs();
  }, [authHeaders]);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim()), 300);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedExternalUserId(externalUserId.trim()), 300);
    return () => clearTimeout(t);
  }, [externalUserId]);

  useEffect(() => {
    const fetchConversations = async () => {
      setConvLoading(true);
      setError(null);
      try {
        const params = new URLSearchParams();
        if (kbId !== 'all') params.set('kb_id', String(kbId));
        params.set('status', status);
        if (debouncedSearch) params.set('search', debouncedSearch);
        if (debouncedExternalUserId) params.set('external_user_id', debouncedExternalUserId);
        if (insufficientOnly) params.set('insufficient_context', 'true');
        params.set('limit', String(limit));

        const res = await api.get(`/ai/conversations?${params.toString()}`, {
          headers: authHeaders,
        });
        setConversations(res.data?.conversations || []);
        setTotal(res.data?.total ?? 0);
      } catch (e: any) {
        console.error(e);
        setError(e?.response?.data?.message || e.message || 'Failed to load conversations');
      } finally {
        setConvLoading(false);
      }
    };

    fetchConversations();
  }, [authHeaders, kbId, status, debouncedSearch, debouncedExternalUserId, insufficientOnly]);

  const kbName = (id: number) =>
    knowledgeBases.find((k) => k.id === id)?.name ?? `KB #${id}`;

  const clearFilters = () => {
    setKbId('all');
    setStatus('active');
    setSearch('');
    setExternalUserId('');
    setInsufficientOnly(false);
  };

  const openMessages = async (conv: Conversation) => {
    setActiveConversation(conv);
    setMessages([]);
    setMessagesError(null);
    setMessagesLoading(true);
    try {
      const res = await api.get(
        `/ai/conversations/${conv.id}/messages?order=asc&limit=500`,
        { headers: authHeaders }
      );
      setMessages(res.data?.messages || []);
    } catch (e: any) {
      console.error(e);
      setMessagesError(e?.response?.data?.message || e.message || 'Failed to load messages');
    } finally {
      setMessagesLoading(false);
    }
  };

  const closeMessages = () => {
    setActiveConversation(null);
    setMessages([]);
    setMessagesError(null);
    setMessagesLoading(false);
  };

  const openConversationState = (conv: Conversation) => {
    setStateModalConversation(conv);
  };

  const closeConversationState = () => {
    setStateModalConversation(null);
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-800">Conversations</h2>
          <p className="text-sm text-gray-500">
            Browse and filter conversations across knowledge bases.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="rounded-full bg-blue-50 text-blue-700 px-3 py-1 text-xs font-semibold">
            Showing {conversations.length} of {total}
          </div>
          <button
            onClick={clearFilters}
            className="px-3 py-1.5 rounded-md border border-gray-300 text-sm text-gray-700 hover:bg-gray-50"
          >
            Reset Filters
          </button>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4">
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          <div className="md:col-span-2">
            <label className="block text-xs font-semibold text-gray-600 mb-1">Search</label>
            <div className="relative">
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search by title..."
                className="w-full rounded-lg border border-gray-300 bg-white pl-10 pr-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-200"
              />
              <svg
                className="absolute left-3 top-2.5 h-4 w-4 text-gray-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-4.35-4.35m0 0A7.5 7.5 0 1010.5 18a7.5 7.5 0 006.15-3.35z" />
              </svg>
            </div>
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1">Knowledge Base</label>
            {kbLoading ? (
              <div className="text-xs text-gray-500">Loading...</div>
            ) : (
              <select
                value={kbId}
                onChange={(e) => setKbId(e.target.value === 'all' ? 'all' : Number(e.target.value))}
                className="w-full text-sm px-2 py-2 border border-gray-300 rounded-lg bg-white"
              >
                <option value="all">All KBs</option>
                {knowledgeBases.map((kb) => (
                  <option key={kb.id} value={kb.id}>
                    {kb.name}
                  </option>
                ))}
              </select>
            )}
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1">External User ID</label>
            <input
              value={externalUserId}
              onChange={(e) => setExternalUserId(e.target.value)}
              placeholder="Filter by external user id..."
              className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-200"
            />
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1">Status</label>
            <select
              value={status}
              onChange={(e) => setStatus(e.target.value as ConvStatusFilter)}
              className="w-full text-sm px-2 py-2 border border-gray-300 rounded-lg bg-white"
            >
              <option value="active">Active</option>
              <option value="archived">Archived</option>
              <option value="all">All</option>
            </select>
          </div>
        </div>

        <div className="mt-4 flex flex-wrap items-center gap-4">
          <label className="inline-flex items-center gap-2 text-sm text-gray-700">
            <input
              type="checkbox"
              checked={insufficientOnly}
              onChange={(e) => setInsufficientOnly(e.target.checked)}
              className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            Only show insufficient-context conversations
          </label>
        </div>
      </div>

      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
          {error}
        </div>
      )}

      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
          <div className="text-sm font-semibold text-gray-700">Conversation List</div>
          {convLoading && <div className="text-xs text-gray-500">Loading...</div>}
        </div>

        {convLoading ? (
          <div className="p-6 text-sm text-gray-500">Loading conversations...</div>
        ) : conversations.length === 0 ? (
          <div className="p-6 text-sm text-gray-500">No conversations match your filters.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead className="bg-gray-50">
                <tr className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  <th className="px-4 py-3">Title</th>
                  <th className="px-4 py-3">KB</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3">Conversation State</th>
                  <th className="px-4 py-3">Messages</th>
                  <th className="px-4 py-3">Updated</th>
                  <th className="px-4 py-3">Flags</th>
                  <th className="px-4 py-3">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {conversations.map((c) => (
                  <tr key={c.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <div className="text-sm font-semibold text-gray-800">
                        {c.title || 'Untitled chat'}
                      </div>
                      <div className="text-xs text-gray-400">ID: {c.id}</div>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700">{kbName(c.kbId)}</td>
                    <td className="px-4 py-3">
                      <span
                        className={
                          c.status === 'active'
                            ? 'inline-flex items-center rounded-full bg-green-100 text-green-800 px-2.5 py-0.5 text-xs font-semibold'
                            : 'inline-flex items-center rounded-full bg-gray-100 text-gray-700 px-2.5 py-0.5 text-xs font-semibold'
                        }
                      >
                        {c.status}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-xs text-gray-600">
                      <button
                        onClick={() => openConversationState(c)}
                        className="text-sm font-semibold text-blue-700 hover:underline"
                      >
                        View State
                      </button>
                      {c.externalUserId ? (
                        <div className="text-[11px] text-gray-400 mt-1">
                          user: {c.externalUserId}
                        </div>
                      ) : null}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700">{c.messageCount}</td>
                    <td className="px-4 py-3 text-sm text-gray-500">
                      {c.updatedAt ? new Date(c.updatedAt).toLocaleString() : 'N/A'}
                    </td>
                    <td className="px-4 py-3">
                      {c.hasInsufficientContext ? (
                        <span className="inline-flex items-center rounded-full bg-yellow-100 text-yellow-800 px-2.5 py-0.5 text-xs font-semibold">
                          insufficient ({c.insufficientContextCount ?? 0})
                        </span>
                      ) : (
                        <span className="text-xs text-gray-400">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <button
                        onClick={() => openMessages(c)}
                        className="text-sm font-semibold text-blue-700 hover:underline"
                      >
                        View Messages
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {activeConversation && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-xl shadow-xl max-w-3xl w-full mx-4 overflow-hidden">
            <div className="px-5 py-4 border-b border-gray-100 flex items-start justify-between gap-4">
              <div>
                <div className="text-lg font-semibold text-gray-800">
                  {activeConversation.title || 'Untitled chat'}
                </div>
                <div className="text-xs text-gray-500">
                  KB: {kbName(activeConversation.kbId)} • ID: {activeConversation.id}
                </div>
              </div>
              <button
                onClick={closeMessages}
                className="text-gray-500 hover:text-gray-700 text-2xl leading-none"
                aria-label="Close"
              >
                &times;
              </button>
            </div>

            <div className="max-h-[70vh] overflow-y-auto p-5 bg-gradient-to-b from-white to-gray-50">
              {messagesLoading ? (
                <div className="text-sm text-gray-500">Loading messages...</div>
              ) : messagesError ? (
                <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
                  {messagesError}
                </div>
              ) : messages.length === 0 ? (
                <div className="text-sm text-gray-500">No messages found.</div>
              ) : (
                <div className="space-y-3">
                  {messages.map((m) => (
                    <div
                      key={`${m.role}-${m.id}-${m.createdAt}`}
                      className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}
                    >
                      <div
                        className={`max-w-[80%] rounded-2xl px-4 py-3 text-sm shadow-sm ${
                          m.role === 'user'
                            ? 'bg-blue-600 text-white'
                            : m.role === 'system'
                              ? 'bg-gray-900 text-white'
                              : 'bg-white text-gray-900 border border-gray-200'
                        }`}
                      >
                        <div className="whitespace-pre-wrap">{m.content}</div>
                        <div
                          className={`mt-2 text-[11px] ${
                            m.role === 'user' ? 'text-blue-100' : 'text-gray-400'
                          }`}
                        >
                          {new Date(m.createdAt).toLocaleString()}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="px-5 py-3 border-t border-gray-100 flex items-center justify-between">
              <div className="text-xs text-gray-500">
                Total messages: {messages.length}
              </div>
              <button
                onClick={closeMessages}
                className="px-3 py-1.5 rounded-md border border-gray-300 text-sm text-gray-700 hover:bg-gray-50"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {stateModalConversation && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-xl shadow-xl max-w-3xl w-full mx-4 overflow-hidden">
            <div className="px-5 py-4 border-b border-gray-100 flex items-start justify-between gap-4">
              <div>
                <div className="text-lg font-semibold text-gray-800">Conversation State</div>
                <div className="text-xs text-gray-500">
                  ID: {stateModalConversation.id}
                </div>
              </div>
              <button
                onClick={closeConversationState}
                className="text-gray-500 hover:text-gray-700 text-2xl leading-none"
                aria-label="Close"
              >
                &times;
              </button>
            </div>

            <div className="max-h-[70vh] overflow-y-auto p-5 bg-gradient-to-b from-white to-gray-50">
              <pre className="whitespace-pre-wrap break-words text-xs text-gray-900 bg-white border border-gray-200 rounded-lg p-4">
                {JSON.stringify(stateModalConversation.conversationState ?? {}, null, 2)}
              </pre>
            </div>

            <div className="px-5 py-3 border-t border-gray-100 flex items-center justify-end">
              <button
                onClick={closeConversationState}
                className="px-3 py-1.5 rounded-md border border-gray-300 text-sm text-gray-700 hover:bg-gray-50"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ConversationBrowser;
